import { type EditorView } from 'prosemirror-view'
import { Selection, TextSelection } from 'prosemirror-state'
import { Schema, Slice } from 'prosemirror-model'
import { Transaction } from 'prosemirror-state'
import { sortBy, sum } from 'lodash'

import { CellSelection } from '../../utils/table/CellSelection'
import { TableMap as PTableMap } from 'prosemirror-tables'
import { TableMap } from '../../utils/table/TableMap'
import { _addRowAt, _addColumnAt } from '../../Commands/table/mutation'
import { setTrackedChangeContext } from '../TrackChanges/utils'
import { ourUuid } from 'utils'

// NOTE:
// Refer to this observable notebook for documentation:
// https://observablehq.com/d/6fa0240b04def648

const isPastableSelection = (schema: Schema, selection: Selection): boolean => {
  const { empty, $from } = selection

  return (
    selection instanceof CellSelection ||
    (empty && ($from.node(-1)?.type === schema.nodes.table_cell || $from.node(-1)?.type === schema.nodes.table_header))
  )
}
const hasPastableContent = (schema: Schema, slice: Slice): boolean =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  slice.content.childCount === 1 && slice.content.firstChild!.type === schema.nodes.table

/**
 * splits out a cell out of a cell which potentially has a rowspan or colspan
 *
 * @param {[number, number]} The coords at which to split out a cell (row, col)
 * @param {TableMap} An up-to-date tablemap for performance reasons
 * @param {Transaction} The transaction in which to modify the table
 * @returns {boolean} `true` iff a cell was split out
 */
const splitOutCell = (coords: readonly [number, number], targetTableMap: TableMap, tr: Transaction): boolean => {
  const trStepCount = tr.steps.length
  const [rowOffset, colOffset] = targetTableMap.getSpanOffsetsAtCoord(...coords)
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const $beforeTargetCell = tr.doc.resolve(targetTableMap.getPosAtCoord(coords[1], coords[0])!)
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const targetCell = $beforeTargetCell.nodeAfter!
  const columnWidths = targetTableMap.getColumnWidths()

  if (colOffset !== 0 || rowOffset !== 0) {
    // we pasted into the middle or end of a cell with some kind of span
    const $beforeMergedCell = tr.doc.resolve(
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      targetTableMap.getPosAtCoord(coords[1] - colOffset, coords[0] - rowOffset)!,
    )

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const mergedCell = $beforeMergedCell.nodeAfter!
    tr.setNodeMarkup($beforeMergedCell.pos, mergedCell.type, {
      ...mergedCell.attrs,
      colspan: colOffset ? colOffset : mergedCell.attrs.colspan,
      rowspan: rowOffset ? rowOffset : mergedCell.attrs.rowspan,
      cellWidth: `${sum(columnWidths.slice(coords[1] - colOffset, coords[1]))}px`,
    })

    // if we still have span left after we split off of the merged cell, reinsert it
    if (colOffset && colOffset > 0) {
      // split off the cell to the right
      tr.insert(
        $beforeMergedCell.pos + mergedCell.nodeSize,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        mergedCell.type.createAndFill({
          ...mergedCell.attrs,
          colspan: 1,
          cellWidth: `${columnWidths[coords[1]]}px`,
        })!,
      )

      if (mergedCell.attrs.colspan - colOffset - 1 >= 1) {
        tr.insert(
          $beforeMergedCell.pos + mergedCell.nodeSize + 4,
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          mergedCell.type.createAndFill({
            ...mergedCell.attrs,
            colspan: mergedCell.attrs.colspan - colOffset - 1,
            cellWidth: `${sum(
              columnWidths.slice(coords[1] + 1, coords[1] + (mergedCell.attrs.colspan - colOffset - 1) + 1),
            )}px`,
          })!,
        )
      }
    }

    if (rowOffset && rowOffset > 0) {
      // insert split off-cell after previous cell in next row, or beginning of row if there is no previous cell
      let insertPos = null
      if (coords[1] - colOffset > 0) {
        const $previousCellNextRow = tr.doc.resolve(
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          targetTableMap.getPosAtCoord(coords[1] - colOffset - 1, coords[0] - rowOffset + 1)!,
        )

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        insertPos = $previousCellNextRow.pos + $previousCellNextRow.nodeAfter!.nodeSize
      } else {
        insertPos = $beforeMergedCell.after(-1)
      }

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tr.insert(insertPos, mergedCell.type.createAndFill({ ...mergedCell.attrs, rowspan: 1 })!)

      // insert potential rest AFTER the cell
      if (mergedCell.attrs.rowspan - rowOffset - 1 >= 1) {
        const map = PTableMap.get(targetTableMap.tableNodeWithPos.node)
        let pos = map.positionAt(coords[0] + 1, coords[1], targetTableMap.tableNodeWithPos.node)

        tr.insert(
          tr.mapping.slice(trStepCount).map(pos + targetTableMap.tableNodeWithPos.posBeforeTable + 1),
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          mergedCell.type.createAndFill({
            ...mergedCell.attrs,
            rowspan: mergedCell.attrs.rowspan - rowOffset - 1,
          })!,
        )
      }
    }

    return true
  } else {
    // we pasted into the start of a cell, which might have a span
    let cellHadSpan = false

    // if we pasted into a cell with rowspan at the start, split off the rest into a new cell in the next row
    if (targetCell.attrs.rowspan > 1) {
      cellHadSpan = true

      tr.setNodeMarkup($beforeTargetCell.pos, targetCell.type, {
        ...targetCell.attrs,
        rowspan: 1,
      })

      // insert split off-cell after previous cell in next row, or beginning of row if there is no previous cell
      const map = PTableMap.get(targetTableMap.tableNodeWithPos.node)
      const insertPos =
        map.positionAt(coords[0] + 1, coords[1], targetTableMap.tableNodeWithPos.node) +
        targetTableMap.tableNodeWithPos.posBeforeTable +
        1

      tr.insert(
        insertPos,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        targetCell.type.createAndFill({ ...targetCell.attrs, rowspan: targetCell.attrs.rowspan - 1 })!,
      )
    }

    // if we pasted into a cell with colspan at the start, split off the rest into a new cell in the same row
    if (targetCell.attrs.colspan > 1) {
      cellHadSpan = true

      tr.setNodeMarkup($beforeTargetCell.pos, targetCell.type, {
        ...targetCell.attrs,
        colspan: 1,
        cellWidth: `${columnWidths[coords[1]]}px`,
        rowspan: 1, // if this was greater 1, it was caught by the code above
      })

      tr.insert(
        $beforeTargetCell.pos + targetCell.nodeSize,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        targetCell.type.createAndFill({
          ...targetCell.attrs,
          colspan: targetCell.attrs.colspan - 1,
          cellWidth: `${sum(columnWidths.slice(coords[1] + 1, coords[1] + targetCell.attrs.colspan))}px`,
          rowspan: 1, // split up remaining cells with a colspan preference instead of rowspan
        })!,
      )
    }

    return cellHadSpan
  }
}

export const pasteIntoTable =
  (schema: Schema) =>
  (view: EditorView, event: Event, slice: Slice): boolean => {
    if (!isPastableSelection(schema, view.state.selection)) return false
    if (!hasPastableContent(schema, slice)) return false

    if (view.state.selection instanceof CellSelection && view.state.selection.isSingleCellSelection) {
      // If we're pasting over a cell selection, delete the cell content, put the cursor inside, then follow the rest of the paste logic.
      const { from, to } = view.state.selection
      const tr = view.state.tr
      tr.delete(from + 2, to - 1)
      tr.setSelection(TextSelection.create(view.state.doc, from + 2))
      view.dispatch(tr)
    }

    // If selected content is inside a cell, then let PM handle the paste functionality
    if (slice.openStart > 3 && slice.openEnd > 3) {
      return false
    }

    const {
      dispatch,
      state: {
        selection: { $from },
        tr,
      },
    } = view

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let inputTable = slice.content.firstChild!
    let inputTableMap = new TableMap({ posBeforeTable: -1, node: inputTable }) // the pos is -1 for convenience reasons, we can use `tableNode.nodeAt` to receive any cell in the table, given its pos as returned by the table map
    let targetTableMap = new TableMap({ posBeforeTable: $from.before(-3), node: $from.node(-3) })
    let [targetStartY, targetStartX] = targetTableMap.getCoordListOfPos($from.before(-1))[0]

    const updateTargetTableMap = () => {
      const $updatedTable = tr.doc.resolve(targetTableMap.tableNodeWithPos.posBeforeTable)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      targetTableMap = new TableMap({ posBeforeTable: $updatedTable.pos, node: $updatedTable.nodeAfter! })
    }

    const processedInputCellPos: number[] = []
    // loop through the coords of the input table
    for (let y = 0; y < inputTableMap.numRows; y++) {
      const targetY = targetStartY + y
      // add row after if pasted content exceeds table size
      if (targetY > targetTableMap.numRows - 1) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const $targetCell = tr.doc.resolve(targetTableMap.getPosAtCoord(targetStartX, targetStartY + y - 1)!)
        _addRowAt($targetCell.end(-1) - 1, tr)

        updateTargetTableMap()
      }

      for (let x = 0; x < inputTableMap.numCols; x++) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const inputCellPos = inputTableMap.getPosAtCoord(x, y)!
        if (processedInputCellPos.includes(inputCellPos)) continue
        processedInputCellPos.push(inputCellPos)

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const inputCell = inputTable.nodeAt(inputCellPos)!
        const targetX = targetStartX + x

        // add column after if pasted content exceeds table size
        if (targetStartX + x > targetTableMap.numCols - 1) {
          _addColumnAt(targetTableMap.tableNodeWithPos.posBeforeTable, tr, targetX)

          updateTargetTableMap()
        }

        if (splitOutCell([targetY, targetX], targetTableMap, tr)) updateTargetTableMap()

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let $targetCell = tr.doc.resolve(targetTableMap.getPosAtCoord(targetX, targetY)!)
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const targetCell = $targetCell.nodeAfter!
        let insertPos = $targetCell.pos

        // make space for the input cell in the target table: delete as many cells as necessary to fit the area covered by the input cell
        let deletePos: number[] = []
        for (let makeSpaceX = targetX; makeSpaceX < targetX + inputCell.attrs.colspan; makeSpaceX++) {
          if (makeSpaceX > targetTableMap.numCols - 1) {
            const stepCount = tr.steps.length
            _addColumnAt(targetTableMap.tableNodeWithPos.posBeforeTable, tr, makeSpaceX)
            updateTargetTableMap()
            deletePos = deletePos.map((pos) => tr.mapping.slice(stepCount).map(pos))
            insertPos = tr.mapping.slice(stepCount).map(insertPos)
          }

          for (let makeSpaceY = targetY; makeSpaceY < targetY + inputCell.attrs.rowspan; makeSpaceY++) {
            const stepCount = tr.steps.length
            if (makeSpaceY > targetTableMap.numRows - 1) {
              _addRowAt(
                targetTableMap.tableNodeWithPos.posBeforeTable + targetTableMap.tableNodeWithPos.node.nodeSize - 2,
                tr,
              )
              updateTargetTableMap()
            }

            if (splitOutCell([makeSpaceY, makeSpaceX], targetTableMap, tr)) updateTargetTableMap()

            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const cellPos = targetTableMap.getPosAtCoord(makeSpaceX, makeSpaceY)!
            deletePos = deletePos.map((pos) => tr.mapping.slice(stepCount).map(pos))
            insertPos = tr.mapping.slice(stepCount).map(insertPos)
            deletePos.push(cellPos)
          }
        }

        sortBy(deletePos)
          .reverse()
          .forEach((pos) => {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const deleteCell = tr.doc.resolve(pos).nodeAfter!

            tr.delete(pos, pos + deleteCell.nodeSize)
          })

        // insert the new cell
        tr.insert(
          insertPos,
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          targetCell.type.create(
            // keep the styles of the cell we paste into
            {
              ...targetCell.attrs,
              colspan: inputCell.attrs.colspan,
              rowspan: inputCell.attrs.rowspan,
              cellWidth: `${sum(targetTableMap.getColumnWidths().slice(targetX, targetX + inputCell.attrs.colspan))}px`,
            },
            inputCell.content,
          )!,
        )

        updateTargetTableMap()
      }
    }

    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch(tr)

    return true
  }
